// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import type { AxiosRequestConfig } from 'axios';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type UploadOrgLogoBody = Record<string, never>;
export const UploadOrgLogoBodySchema = z.object({

}).strict();

export type UploadOrgFaviconBody = Record<string, never>;
export const UploadOrgFaviconBodySchema = z.object({

}).strict();

export type BrandFont = "outfit" | "system" | "serif";
export const BrandFontSchema = z.enum(["outfit", "system", "serif"] as const);

export type OrgBranding = {
  organizationName: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  primaryForeground: string | null;
  accentColor: string | null;
  accentForeground: string | null;
  faviconUrl: string | null;
  font: BrandFont | null;
  updatedAt: string | null;
};
export const OrgBrandingSchema = z.object({
  organizationName: z.string().nullable(),
  logoUrl: z.string().nullable(),
  primaryColor: z.string().nullable(),
  primaryForeground: z.string().nullable(),
  accentColor: z.string().nullable(),
  accentForeground: z.string().nullable(),
  faviconUrl: z.string().nullable(),
  font: BrandFontSchema.nullable(),
  updatedAt: z.string().nullable(),
}).strict();

export type SetOrgBrandingBody = {
  organizationName?: string | null | undefined;
  primaryColor?: string | null | undefined;
  accentColor?: string | null | undefined;
  font?: BrandFont | null | undefined;
};
export const SetOrgBrandingBodySchema = z.object({
  organizationName: z.string().min(1).max(200).nullable().optional(),
  primaryColor: z.string().regex(new RegExp("^#[0-9a-fA-F]{6}$"), "must be a 6-digit hex color like #1a73e8").nullable().optional(),
  accentColor: z.string().regex(new RegExp("^#[0-9a-fA-F]{6}$"), "must be a 6-digit hex color like #1a73e8").nullable().optional(),
  font: BrandFontSchema.nullable().optional(),
}).strict().refine((body) => Object.keys(body).length > 0);

export type GetOrgBrandingResponse = {
  data: OrgBranding;
};

export const GetOrgBrandingParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function getOrgBranding(orgId: string): Promise<GetOrgBrandingResponse> {
  const response = await api.get<GetOrgBrandingResponse>(`/orgs/${encodeURIComponent(orgId)}/branding`);
  return response.data;
}

export type SetOrgBrandingResponse = {
  data: OrgBranding;
};

export const SetOrgBrandingParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function setOrgBranding(orgId: string, body: SetOrgBrandingBody): Promise<SetOrgBrandingResponse> {
  const response = await api.patch<SetOrgBrandingResponse>(`/orgs/${encodeURIComponent(orgId)}/branding`, body);
  return response.data;
}

export type UploadOrgBrandingLogoResponse = {
  data: OrgBranding;
};

export const UploadOrgBrandingLogoParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function uploadOrgBrandingLogo(orgId: string, body: UploadOrgLogoBody, file: Blob, options?: { signal?: AbortSignal; onUploadProgress?: (progress: { loaded: number; total?: number }) => void }): Promise<UploadOrgBrandingLogoResponse> {
  const form = new FormData();
  form.append("file", file);
  Object.entries(body as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    form.append(key, String(value));
  });
  const config: AxiosRequestConfig = { headers: { 'Content-Type': 'multipart/form-data' } };
  if (options?.signal) config.signal = options.signal;
  const onProgress = options?.onUploadProgress;
  if (onProgress) {
    config.onUploadProgress = (event) =>
      onProgress(event.total === undefined ? { loaded: event.loaded } : { loaded: event.loaded, total: event.total });
  }
  const response = await api.post<UploadOrgBrandingLogoResponse>(`/orgs/${encodeURIComponent(orgId)}/branding/logo`, form, config);
  return response.data;
}

export type UploadOrgBrandingFaviconResponse = {
  data: OrgBranding;
};

export const UploadOrgBrandingFaviconParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function uploadOrgBrandingFavicon(orgId: string, body: UploadOrgFaviconBody, file: Blob, options?: { signal?: AbortSignal; onUploadProgress?: (progress: { loaded: number; total?: number }) => void }): Promise<UploadOrgBrandingFaviconResponse> {
  const form = new FormData();
  form.append("file", file);
  Object.entries(body as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    form.append(key, String(value));
  });
  const config: AxiosRequestConfig = { headers: { 'Content-Type': 'multipart/form-data' } };
  if (options?.signal) config.signal = options.signal;
  const onProgress = options?.onUploadProgress;
  if (onProgress) {
    config.onUploadProgress = (event) =>
      onProgress(event.total === undefined ? { loaded: event.loaded } : { loaded: event.loaded, total: event.total });
  }
  const response = await api.post<UploadOrgBrandingFaviconResponse>(`/orgs/${encodeURIComponent(orgId)}/branding/favicon`, form, config);
  return response.data;
}

export const orgBrandingKeys = {
  all: ["orgBranding"] as const,
  lists: () => [...orgBrandingKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...orgBrandingKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...orgBrandingKeys.all, 'detail'] as const,
  detail: (id: string) => [...orgBrandingKeys.details(), id] as const,
};
